import type { EncryptedMediaManifest } from '../media/e2eeMediaTypes';

const KEY = 'intimsync_web_media_outbox_v1';
const MAX_BACKOFF_MS = 1000 * 60 * 30;

export type WebMediaOutboxItem = {
  id: string;
  createdAt: number;
  updatedAt: number;
  retryCount: number;
  nextAttemptAt: number;
  lastError?: string;
  manifest: EncryptedMediaManifest;
  localObjectUrl?: string;
  previewObjectUrl?: string;
};

function readAll(): WebMediaOutboxItem[] {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return [];
    const items = JSON.parse(raw);
    return Array.isArray(items) ? items : [];
  } catch {
    return [];
  }
}

function writeAll(items: WebMediaOutboxItem[]) {
  window.localStorage.setItem(KEY, JSON.stringify(items));
}

function computeBackoffMs(retryCount: number): number {
  const base = Math.max(1, retryCount || 0);
  return Math.min(MAX_BACKOFF_MS, 1500 * Math.pow(2, base - 1));
}

export async function listWebMediaOutbox(now = Date.now()): Promise<WebMediaOutboxItem[]> {
  return readAll()
    .filter((x) => (x.nextAttemptAt || 0) <= now)
    .sort((a, b) => a.createdAt - b.createdAt);
}

export async function listAllWebMediaOutbox(): Promise<WebMediaOutboxItem[]> {
  return readAll().sort((a, b) => a.createdAt - b.createdAt);
}

export async function enqueueWebMediaOutbox(
  manifest: EncryptedMediaManifest,
  localObjectUrl?: string,
  previewObjectUrl?: string
): Promise<WebMediaOutboxItem> {
  const now = Date.now();
  const item: WebMediaOutboxItem = {
    id: `web_media_outbox_${now}_${Math.random().toString(36).slice(2, 10)}`,
    createdAt: now,
    updatedAt: now,
    retryCount: 0,
    nextAttemptAt: now,
    manifest,
    localObjectUrl,
    previewObjectUrl,
  };
  const items = readAll();
  items.push(item);
  writeAll(items);
  return item;
}

export async function removeWebMediaOutbox(id: string): Promise<void> {
  writeAll(readAll().filter((x) => x.id !== id));
}

export async function markWebMediaOutboxFailure(id: string, error?: string): Promise<void> {
  const now = Date.now();
  writeAll(
    readAll().map((x) => {
      if (x.id !== id) return x;
      const retryCount = (x.retryCount || 0) + 1;
      return {
        ...x,
        retryCount,
        updatedAt: now,
        nextAttemptAt: now + computeBackoffMs(retryCount),
        lastError: error ? String(error).slice(0, 400) : x.lastError,
      };
    })
  );
}

export async function enqueueMediaManifest(payload: {
  mediaId: string;
  conversationId: string;
  recipientUserId?: string | null;
  mediaKind: 'image' | 'audio' | 'video';
  mimeType: string;
  sizeBytes: number;
  manifest: EncryptedMediaManifest;
}) {
  return enqueueWebMediaOutbox(payload.manifest);
}
